import { useEffect, useState } from "react";
import { fetchTimeLogs } from "../api/timeLog";
import { useAppContext } from "../context/AppContext";
import { Avatar, Box, Typography } from "@mui/material";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { generateColorFromId } from "../utils/utilFunctions";
import { useNavigate } from "react-router-dom";

const locales = {
    "en-US": require("date-fns/locale/en-US"),
};

const TeamActivity = () => {
    const { employeesAppContext, user } = useAppContext();
    const [timeLogs, setTimeLogs] = useState([]);
    const [filteredTimeLogs, setFilteredTimeLogs] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [viewDate, setViewDate] = useState(new Date()); // Keep track of current view date
    const navigate = useNavigate();

    const localizer = dateFnsLocalizer({
        format,
        parse: (date) => parse(date, "yyyy-MM-dd", new Date()),
        startOfWeek: () => startOfWeek(new Date()),
        getDay,
        locales,
    });

    useEffect(() => {
        if (!selectedDate) {
            setFilteredTimeLogs(timeLogs);
            return;
        }

        const filteredLogs = timeLogs.filter(log => {
            const logDate = new Date(log.logged_date).toISOString().split('T')[0];
            return logDate === selectedDate;
        });

        setFilteredTimeLogs(filteredLogs);
    }, [selectedDate, timeLogs]);

    const loadTimeLogs = async () => {
        try {
            const data = await fetchTimeLogs();
            setTimeLogs(data);
            setFilteredTimeLogs(data);
        } catch (err) {
            console.log('error fetching time logs: ', err);
        }
    };

    useEffect(() => {
        loadTimeLogs();
    }, []);

    const groupTimeLogsByUserAndDate = (logs) => {
        const groupedLogs = {};

        logs.forEach(log => {
            const logDate = new Date(log.logged_date).toISOString().split('T')[0];
            const userId = log.user_id;

            if (!groupedLogs[userId]) {
                groupedLogs[userId] = {};
            }

            if (!groupedLogs[userId][logDate]) {
                groupedLogs[userId][logDate] = 0;
            }

            groupedLogs[userId][logDate] += log.logged_minutes;
        });

        return groupedLogs;
    };

    const groupedTimeLogs = groupTimeLogsByUserAndDate(filteredTimeLogs);

    const convertGroupedLogsToEvents = () => {
        const events = [];

        Object.keys(groupedTimeLogs).forEach((userId) => {
            Object.keys(groupedTimeLogs[userId]).forEach((logDate) => {
                const totalMinutes = groupedTimeLogs[userId][logDate];

                const employee = employeesAppContext.find(emp => String(emp.id) === String(userId));

                if (employee) {
                    const timeString = `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
                    events.push({
                        title: `${employee.name}: ${timeString}`,
                        user_id: userId,
                        start: new Date(logDate),
                        end: new Date(logDate),
                        logged_minutes: totalMinutes,
                        allDay: true,
                        id: `${userId}-${logDate}`,
                    });
                } else {
                    console.log(`Employee not found for user_id: ${userId}`);
                }
            });
        });

        return events;
    };

    const handleEventSelect = (date) => {
        const selectedDate = date.start.toISOString().split('T')[0];
        navigate(`/team?selectedDate=${selectedDate}`);
    };

    const handleSlotSelect = (slotInfo) => {
        const selectedDate = new Date(slotInfo.end);
        selectedDate.setHours(0, 0, 0, 0);
        const formattedDate = selectedDate.toISOString().split('T')[0];
        navigate(`/team?selectedDate=${formattedDate}`);
    };

    const onNavigate = (newDate) => {
        console.log(newDate)
        setViewDate(newDate); // Update the view date when navigating
    };

    const CustomEvent = ({ event }) => {
        const employee = employeesAppContext.find((emp) => String(emp.id) === String(event.user_id));

        if (!employee) {
            return <Box>Employee Not Found</Box>;
        }

        const timeString = `${Math.floor(event.logged_minutes / 60)}h ${event.logged_minutes % 60}m`;
        return (
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Avatar
                    sx={{
                        width: 24,
                        height: 24,
                        color: 'black',
                        cursor: 'pointer',
                        backgroundColor: employee
                            ? generateColorFromId(`${employee.id}${employee.id}${employee.id}`)
                            : 'gray',
                    }}
                >
                    {employee && employee.name[0]}
                </Avatar>
                <Typography fontWeight={'bold'}>
                    {employee.name.substring(0, 5)}
                </Typography>
                <Typography>
                    {timeString}
                </Typography>
            </Box>
        );
    };

    return (
        <Box sx={{ height: '100vh', width: '100%' }}>
            <Calendar
                localizer={localizer}
                events={convertGroupedLogsToEvents()}
                startAccessor="start"
                endAccessor="end"
                components={{
                    event: CustomEvent,
                }}
                selectable
                style={{ height: '100%', width: '100%' }}
                views={['month']}
                defaultView="month"
                date={viewDate} // Add this prop to bind the view date
                onSelectSlot={handleSlotSelect}
                onSelectEvent={handleEventSelect}
                onNavigate={onNavigate} // Handle the navigation
            />
        </Box>
    );
};

export default TeamActivity;
